import { Fragment, useEffect } from 'react';
import cn from 'clsx';

import toast from 'react-hot-toast';
import { Dialog as HUIDialog, Transition } from '@headlessui/react';
import { CrossIcon } from '@components/elements/Icons';
import { formatPollData, FormattedPoll } from '@utils/Poll';
import useSwr from 'swr';
import { useAuth } from '@hooks/Auth';
import { listPollsByOwner } from '@sdk/managers/PollsManager';
import { PollStatus } from '@sdk/API';

const getPollData = async (
  _query: string,
  owner: string,
  companyId: string,
  filter: 'VOTER' | 'PROXY'
) => {
  const filters = [
    { status: { ne: PollStatus.CLOSED } },
    { status: { ne: PollStatus.END } },
    { companyID: { attributeExists: true } },
    { companyID: { eq: companyId } },
  ];

  if (filter === 'VOTER')
    filters.push({ status: { ne: PollStatus.PLEDGING1 } });

  const getListPolls: any = await listPollsByOwner(
    [
      {
        items: [
          'id',
          'title',
          'description',
          'status',
          'owner',
          'startTime',
          'endTime',
          'companyID',
          'image { key bucket region }',
          'video',
          'createdAt',
          {
            options: ['id', 'description'],
          },
          { voteResults: [{ items: ['count'] }] },
        ],
      },
      'nextToken',
    ],
    owner,
    {
      and: filters,
    }
  );

  const {
    data: {
      result: { items },
    },
  } = getListPolls;

  const formattedPolls = (await Promise.all(
    items.map(formatPollData)
  )) as FormattedPoll[];

  formattedPolls.sort((a: FormattedPoll, b: FormattedPoll) => {
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  });

  return formattedPolls;
};

interface PollPickerDialogProps {
  open?: boolean;
  onCancel?: () => any;
  onSelect?: (poll: FormattedPoll) => any;
  title?: string;
  className?: string;
  filter?: 'VOTER' | 'PROXY';
}

export function PollPickerDialog({
  open = false,
  onCancel,
  onSelect,
  title,
  filter = 'PROXY',
  className,
}: PollPickerDialogProps): React.ReactElement {
  const { companyAdmin, company } = useAuth();

  const { data, error } = useSwr<FormattedPoll[]>(
    companyAdmin && company
      ? ['/admin/polls', companyAdmin.owner, company.companyID, filter]
      : null,
    getPollData,
    {
      revalidateOnFocus: true,
    }
  );

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  return (
    <Transition appear show={open} as={Fragment}>
      <HUIDialog
        open={open}
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        onClose={() => onCancel?.()}
      >
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <HUIDialog.Overlay className="fixed inset-0 bg-black bg-opacity-70" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div
              className={cn(
                'inline-block w-full max-w-md text-left align-middle transition-all transform bg-white shadow-xl rounded-lg pb-2',
                className
              )}
              style={{
                height: '80vh',
                maxHeight: '80vh',
              }}
            >
              <button
                className="absolute top-4 right-4 focus:outline-none"
                onClick={() => onCancel?.()}
              >
                <CrossIcon />
              </button>
              {title && (
                <HUIDialog.Title
                  as="h3"
                  className="px-4 py-6 text-lg font-bold leading-6"
                >
                  {title}
                </HUIDialog.Title>
              )}

              <div
                className="max-w-full overflow-y-scroll"
                style={{ height: '90%' }}
              >
                {data?.map((poll) => (
                  <div
                    key={`pollpicker-${poll.id}`}
                    role="button"
                    onClick={
                      onSelect
                        ? () => {
                            onCancel?.();
                            onSelect(poll);
                          }
                        : onCancel
                    }
                    className="flex items-center px-4 py-2 space-x-2 border-b border-black select-none border-opacity-10 hover:bg-black hover:bg-opacity-10 "
                  >
                    <div>
                      <div className="w-10 h-10 rounded-full">
                        <img
                          className="object-cover w-full h-full rounded-full"
                          src={poll.image?.url}
                          alt="poll"
                        />
                      </div>
                    </div>
                    <div className="overflow-hidden font-semibold leading-6 break-words">
                      {poll.title}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Transition.Child>
        </div>
      </HUIDialog>
    </Transition>
  );
}

export default PollPickerDialog;
